import { cn } from "@/lib/utils";
import { ModalType, setModal } from "@/store/globalSettingsSlice";
import { setSelectedElementKey } from "@/store/iframeSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import {
  getNewStoryboardPosition,
  getUnexpiredToken,
  useActiveCanvas,
  useCanvasId,
} from "@/utils/utils";
import * as Lucide from "lucide-react";
import {
  ArrowUp,
  ChevronLeft,
  ImageUp,
  MousePointerClick,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useImageUploader } from "../AppHeader/FileUpload";
import { Button } from "../ui/button";
import ContextButton from "./components/ContextButton";

import { ACTION_TYPES } from "@/utils/ai/utils";
import axios from "axios";
import { useSession } from "@clerk/clerk-react";
import { useCreateImageStoryboard } from "../Canvas/hooks/useCopyPaste";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { useAppDispatch } from "@/store/store";
import { fetchFileTree } from "@/store/codebaseSlice";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { TempoElement } from "@/utils/tempoElement";

interface Props {
  addContextMode?: boolean;
}

enum ReferenceObjectType {
  Storyboard,
  LucideIcon,
  Component,
}

interface ReferenceObject {
  id: string;
  type: ReferenceObjectType;
  display: string;
  icon?: React.ComponentType<any>;
}

const GenerateImageInput: React.FC<Props> = React.memo(({ addContextMode }) => {
  const canvasId = useCanvasId();

  const { selectedNavTreeNode, selectedElement, storyboardIdSelected } =
    useSelectedNavTreeNode();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const activeCanvas = useActiveCanvas();
  const activeCanvasRef = useRef(activeCanvas);
  useEffect(() => {
    activeCanvasRef.current = activeCanvas;
  }, [activeCanvas]);

  const {
    uploadedFiles,
    uploadFiles,
    clearUploadedFiles,
    FilePreviewerComponent,
    FileUploadComponent,
  } = useImageUploader();

  const setActionType = useAiActionStore((state) => state.setActionType);
  const actionType = useAiActionStore((state) => state.actionType);

  const [isImageLoading, setIsImageLoading] = useState(false);

  const dispatch = useAppDispatch();

  const [promptInput, setPromptInput] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [editModeHovered, setEditModeHovered] = useState(false);

  const setAddContextMode = useLayoutStore((state) => state.setAddContextMode);

  const references: ReferenceObject[] = useMemo(() => {
    const storyboards = (activeCanvas?.storyboards || [])
      .filter((storyboard) => storyboard.deleted !== true)
      .filter((storyboard) => storyboard.type !== STORYBOARD_TYPES.APPLICATION)
      .map((storyboard) => {
        return {
          id: `storyboard-${storyboard.id}`,
          type: ReferenceObjectType.Storyboard,
          display: storyboard.name,
        };
      });

    const components = Object.values(treeElementLookup)
      .filter((treeElement) => {
        return (
          treeElement.type === "component" ||
          treeElement.type === "storybook-component"
        );
      })
      .map((treeElement: any) => {
        return {
          id: `component-${treeElement.codebaseId}`,
          type: ReferenceObjectType.Component,
          display: treeElement.componentName,
        };
      });

    const icons: ReferenceObject[] = Object.keys(Lucide).map((iconName) => {
      return {
        id: `lucide-icon-${iconName}`,
        type: ReferenceObjectType.LucideIcon,
        display: iconName,
        icon: Lucide[iconName],
      };
    });

    return [...storyboards, ...components, ...icons];
  }, [activeCanvas, treeElementLookup]);

  const queryIcons = useCallback(
    (query: string, callback: (data: ReferenceObject[]) => void) => {
      if (query.length <= 2) return;

      const matches = references
        .filter(
          (reference) => reference.type === ReferenceObjectType.LucideIcon,
        )
        .filter((reference) => {
          return (
            reference.display.toLowerCase().indexOf(query.toLowerCase()) > -1
          );
        });
      callback(matches);
    },
    [references],
  );

  useEffect(() => {
    if (addContextMode) {
      setActionType(ACTION_TYPES.ADD_CONTEXT);
      return;
    }

    if (selectedElement.isEmpty()) {
      setActionType(ACTION_TYPES.GENERATE);
    } else {
      setActionType(ACTION_TYPES.EDIT);
    }
  }, [addContextMode, selectedElement, setActionType]);

  useEffect(() => {
    if (addContextMode) {
      setActionType(ACTION_TYPES.ADD_CONTEXT);
      return;
    }

    if (!selectedElement.isEmpty()) {
      setActionType(ACTION_TYPES.EDIT);
    }
  }, [selectedElement, addContextMode, setActionType]);

  useEffect(() => {
    if (actionType === ACTION_TYPES.EDIT && selectedElement.isEmpty()) {
      setActionType(ACTION_TYPES.GENERATE);
    }
  }, [selectedElement, actionType, setActionType]);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const { session } = useSession();
  const createImageStoryboard = useCreateImageStoryboard();

  const handleSubmit = useCallback(async () => {
    if (!session) {
      return;
    }

    if (!promptInput || promptInput.trim() === "" || isImageLoading) {
      return;
    }

    const filesToAttach = [...uploadedFiles].filter(Boolean);

    toast.info(`Generating your image...`);

    const supabaseJWT = await getUnexpiredToken(session);

    setIsImageLoading(true);

    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/ai/vercel-ai/aiGenerateImage`,
        {
          image_prompt: promptInput,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      if (response.data.status === 400) {
        if (response.data.errorData?.code === "moderation_blocked") {
          toast.error(
            "Your request was blocked by OpenAI's moderation system.",
          );
          setIsImageLoading(false);
          return;
        } else {
          throw new Error(
            response.data.errorData?.message || response.data.error,
          );
        }
      }

      dispatch(
        fetchFileTree({
          session,
          canvasId,
        }),
      );

      usePlanInfoStore.getState().refreshPlanInfo(session);

      const {
        filePath,
        dimensions: { width, height },
      } = response.data.data.payload;
      const { x, y } = getNewStoryboardPosition(
        width,
        height,
        activeCanvasRef.current?.storyboards || [],
      );

      await createImageStoryboard({
        filePath,
        positionX: x,
        positionY: y,
        actionText: "Created storyboard for generated image",
        width,
        height,
        altText: "Generated Image",
      });

      setPromptInput("");
      clearUploadedFiles();
      track("AI_PROMPT_REQUESTED", {
        type: "AI_IMAGE_GENERATION",
        prompt: promptInput,
        filesToAttach: filesToAttach.map((f) => f.name),
        actionType,
      });
      setIsImageLoading(false);
    } catch (error) {
      console.error("Error generating image:", error);
      toast.error("Failed to generate image");
      setIsImageLoading(false);
    }
  }, [
    promptInput,
    isImageLoading,
    uploadedFiles,
    actionType,
    setPromptInput,
    clearUploadedFiles,
    dispatch,
    canvasId,
    createImageStoryboard,
    session,
  ]);

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isImageLoading) {
        return;
      }
      setPromptInput(event.target.value);
    },
    [isImageLoading, setPromptInput],
  );

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [promptInput]);

  useEffect(() => {
    const handleFocus = () => setIsInputFocused(true);
    const handleBlur = () => setIsInputFocused(false);

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("focus", handleFocus);
      inputElement.addEventListener("blur", handleBlur);
    }

    inputRef.current?.focus();

    return () => {
      if (inputElement) {
        inputElement.removeEventListener("focus", handleFocus);
        inputElement.removeEventListener("blur", handleBlur);
      }
    };
  }, []);

  useEffect(() => {
    if (isImageLoading) {
      dispatch(setSelectedElementKey(null));
    }
  }, [isImageLoading, dispatch]);

  const clearContext = () => {
    dispatch(setSelectedElementKey(null));
  };

  const actionText = useMemo(() => {
    if (isImageLoading) {
      return "Generating image...";
    }
    if (isDragging) {
      return "Drop image to use as context";
    }
    return "Describe your image...";
  }, [isDragging, isImageLoading]);

  const handleImagePaste = (
    event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>,
  ) => {
    if (event.defaultPrevented) return;
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          uploadFiles([file]);
          return;
        }
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files.length > 0 && files[0].type.startsWith("image/")) {
      uploadFiles(Array.from(files));
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    setIsDragging(false);
  };

  const container = useRef(null); // For mentions popup portal

  const showAddedContextButton =
    (actionType === ACTION_TYPES.EDIT ||
      actionType === ACTION_TYPES.ADD_CONTEXT) &&
    !selectedElement.isEmpty();

  const addedContextButton = showAddedContextButton ? (
    <ContextButton
      element={selectedElement}
      // @ts-expect-error TS2322 - Assume navTreeNode type is okay or fix if needed
      navTreeNode={selectedNavTreeNode}
      onClick={() => {
        clearContext();
        if (!addContextMode) {
          setActionType(ACTION_TYPES.GENERATE);
        }
      }}
      storyboardSelected={Boolean(storyboardIdSelected)}
    />
  ) : null;

  const {
    remainingUsage: { hasPromptsLeft },
  } = useUsageLimits();

  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    const imageGenerationTimeEstimate = 20 * 1000;
    if (isImageLoading) {
      setLoadingProgress(0);
      const interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const newProgress = prev + 1;
          return newProgress <= 100 ? newProgress : 100;
        });
      }, imageGenerationTimeEstimate / 100);
      return () => {
        clearInterval(interval);
        setLoadingProgress(0);
      };
    }
  }, [isImageLoading]);

  return (
    <div className="flex w-full flex-col relative">
      {/* Loading Indicator Container */}
      <div
        className={cn(
          "w-full h-1 bg-background-primary rounded-lg absolute top-[-8px] left-0 overflow-hidden p-0.5",
          "transition-opacity duration-250 ease-in-out",
          isImageLoading ? "opacity-100" : "opacity-0",
        )}
      >
        {/* Loading Bar */}
        <div
          className={cn(
            "absolute top-0 left-0 h-full bg-primary-100 transition-all ease-linear",
          )}
          style={{ width: `${loadingProgress}%` }}
        />
      </div>

      <div className="primary rounded-lg bg-background-transparent">
        <div
          className={cn(
            "flex flex-col p-1.5 h-auto bg-background-primary w-full rounded-lg shadow-lg pointer-events-auto relative",
            isDragging && "border border-primary-500 border-dashed",
            isInputFocused && "border-primary-0",
          )}

          // Disable image handling for now
          // onDragEnter={handleDragEnter}
          // onDragOver={handleDragOver}
          // onDragLeave={handleDragLeave}
          // onDrop={handleDrop}
          // onPaste={handleImagePaste}
        >
          {/* File previews and context buttons */}
          {uploadedFiles.length > 0 && (
            <div
              className={cn(
                "flex flex-row items-center justify-start gap-1 pl-2 flex-wrap",
                showAddedContextButton || uploadedFiles.length > 0
                  ? "pt-1 pb-1"
                  : "pb-1",
              )}
            >
              <FilePreviewerComponent />
            </div>
          )}
          {/* Input row */}
          <div
            className={cn(
              "flex w-full flex-row justify-center items-end text-2xs text-white leading-none relative",
            )}
          >
            <div ref={container}></div> {/* Mentions portal host */}
            <div
              className={cn(
                "flex items-end justify-center transition-all duration-200 ease-in-out w-full max-w-full gap-0.5",
              )}
            >
              {/* Back button - keep if this input is shown in a modal-like view */}
              <Button
                variant="ghost"
                className={cn(
                  "h-7 w-7 p-1 shrink-0 rounded-md bg-background-secondary group",
                )}
                onClick={() => dispatch(setModal(ModalType.None))}
              >
                <ChevronLeft className="size-4 text-icons-secondary group-hover:text-icons-primary" />
              </Button>

              {/* Prompt Input Area */}
              <div className={cn("flex-1 flex items-end gap-0.5")}>
                <MentionsInput
                  inputRef={inputRef}
                  className={cn(
                    "mentions-input min-w-[150px] min-h-7 max-h-32 overflow-y-auto mx-1 focus:outline-hidden outline-hidden bg-transparent text-xs font-medium w-full resize-none", // Allow vertical resize via scroll, resize-none
                  )}
                  style={{ height: "auto" }}
                  value={promptInput}
                  onChange={handleInput}
                  placeholder={
                    hasPromptsLeft
                      ? actionText
                      : "No credits available. You can still make changes in Tempo, but AI-powered features won't be available."
                  }
                  disabled={
                    (actionType !== ACTION_TYPES.GENERATE &&
                      selectedElement.isEmpty()) ||
                    isImageLoading ||
                    !hasPromptsLeft
                  }
                  rows={1}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !isImageLoading &&
                      promptInput.trim()
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmit();
                    }
                  }}
                  allowSuggestionsAboveCursor
                  suggestionsPortalHost={container.current}
                  onPaste={handleImagePaste}
                  singleLine={false}
                >
                  <Mention
                    data={references.filter(
                      (reference) =>
                        reference.type === ReferenceObjectType.Storyboard ||
                        reference.type === ReferenceObjectType.Component,
                    )}
                    displayTransform={(id, display) => `@${display}`}
                    // trigger="@"
                    trigger="tempo-disabled-for-now"
                    renderSuggestion={(
                      suggestion,
                      search,
                      highlightedDisplay,
                    ) => <div className="flex h-max">{highlightedDisplay}</div>}
                    markup="@[__display__](__id__)"
                    className="bg-primary-200"
                    appendSpaceOnAdd
                  />
                  <Mention
                    data={queryIcons}
                    displayTransform={(id, display) => `<${display} />`}
                    markup=":[__display__](__id__)"
                    // trigger=":"
                    trigger="tempo-disabled-for-now"
                    renderSuggestion={(
                      suggestion,
                      search,
                      highlightedDisplay,
                    ) => {
                      const Icon = suggestion.icon;
                      return (
                        <div className="flex flex-row justify-between items-center space-x-1">
                          <span>{highlightedDisplay}</span>
                          {Icon && <Icon className="w-4 h-4 shrink-0" />}{" "}
                        </div>
                      );
                    }}
                    className="bg-primary-200"
                    appendSpaceOnAdd
                  />
                </MentionsInput>
              </div>

              {/* Buttons on the right */}
              {isDragging && (
                <Button
                  variant="ghost"
                  className="h-7 w-7 p-1 rounded-md shrink-0"
                >
                  <ImageUp
                    strokeWidth={1}
                    className="w-5 h-5 text-icons-muted group-hover:text-icons-primary"
                  />
                </Button>
              )}

              {/* File Upload Button - Disabled for now */}
              {false &&
                !isDragging &&
                hasPromptsLeft && ( // Check prompt limits if needed
                  <FileUploadComponent className="h-7 w-7 p-1 shrink-0" /> // Add shrink-0
                )}

              {/* Pick & Edit / Add Context Button - Disabled for now */}
              {false && hasPromptsLeft && (
                <div className="h-full items-center justify-center flex shrink-0">
                  {/* Conditionally render ContextButton OR Pick Element Button */}
                  {showAddedContextButton ? (
                    addedContextButton // Render the context button if shown
                  ) : (
                    // Otherwise render the Pick Element button
                    <Button
                      variant="ghost"
                      className={cn(
                        "p-1 w-7 h-7 rounded-md group shrink-0",
                        (actionType === ACTION_TYPES.EDIT ||
                          actionType === ACTION_TYPES.ADD_CONTEXT) &&
                          !selectedElement.isEmpty() && // Only highlight if element is selected
                          "bg-primary-0",
                        isImageLoading && "opacity-70",
                      )}
                      onClick={() => {
                        if (isImageLoading) return;
                        setEditModeHovered(false);
                        if (addContextMode) {
                          clearContext(); // Clears selection
                          setAddContextMode(false); // Exit add context mode
                          setActionType(ACTION_TYPES.GENERATE); // Reset to generate
                          return;
                        }
                        if (actionType === ACTION_TYPES.EDIT) {
                          clearContext();
                          setActionType(ACTION_TYPES.GENERATE);
                        } else {
                          clearContext();
                          setActionType(ACTION_TYPES.EDIT);
                          toast.info(
                            "Select an element on the canvas to add context.",
                          );
                        }
                      }}
                      onMouseEnter={() => setEditModeHovered(true)}
                      onMouseLeave={() => setEditModeHovered(false)}
                    >
                      {editModeHovered && actionType === ACTION_TYPES.EDIT ? (
                        <X
                          data-tooltip-content={"Exit Pick Element Mode"}
                          data-tooltip-id="tooltip"
                          className="w-4 h-4 text-icons-muted group-hover:text-icons-primary cursor-pointer focus:outline-hidden"
                        />
                      ) : (
                        <MousePointerClick
                          data-tooltip-content={
                            actionType === ACTION_TYPES.EDIT
                              ? "Pick Element Mode Active (Click to Exit)"
                              : "Pick Element for Context"
                          }
                          data-tooltip-id="tooltip"
                          className={cn(
                            "w-4 h-4 text-icons-muted group-hover:text-icons-primary cursor-pointer focus:outline-hidden",
                            (actionType === ACTION_TYPES.EDIT ||
                              actionType === ACTION_TYPES.ADD_CONTEXT) &&
                              !selectedElement.isEmpty() &&
                              "text-white",
                          )}
                        />
                      )}{" "}
                    </Button>
                  )}
                </div>
              )}

              {/* Submit Button Area */}
              {hasPromptsLeft && (
                <div
                  className={cn(
                    "transition-all duration-200 pl-1 border-l border-l-border-primary! shrink-0 flex items-center",
                    // Disable submit if edit/context mode is active but no element selected
                    (actionType === ACTION_TYPES.EDIT ||
                      actionType === ACTION_TYPES.ADD_CONTEXT) &&
                      selectedElement.isEmpty() &&
                      "opacity-50 cursor-not-allowed pointer-events-none", // Grey out instead of hiding
                  )}
                >
                  {isImageLoading ? (
                    <Button
                      variant="primary"
                      disabled={true}
                      className="h-7 w-7 p-1 rounded-md shrink-0"
                    >
                      <div className={cn("animate-spin")}>
                        <Lucide.Loader2 className="h-4 w-4 text-secondary-900" />
                      </div>
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={
                        isImageLoading ||
                        !promptInput.trim() ||
                        ((actionType === ACTION_TYPES.EDIT ||
                          actionType === ACTION_TYPES.ADD_CONTEXT) &&
                          selectedElement.isEmpty())
                      }
                      className="h-7 w-7 p-1 rounded-md shrink-0"
                      onClick={handleSubmit}
                    >
                      <ArrowUp
                        data-tooltip-content="Generate Image"
                        data-tooltip-id="tooltip"
                        data-tooltip-place="bottom"
                        className={cn(
                          "size-4 text-secondary-900 cursor-pointer focus:outline-hidden",
                          (isImageLoading ||
                            !promptInput.trim() ||
                            ((actionType === ACTION_TYPES.EDIT ||
                              actionType === ACTION_TYPES.ADD_CONTEXT) &&
                              selectedElement.isEmpty())) &&
                            "opacity-50",
                        )}
                      />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default GenerateImageInput;
